import { useEffect, useRef, useState } from "react";
export const minSafeInteger = Number.MIN_SAFE_INTEGER || -9007199254740991;
export const maxSafeInteger = Number.MAX_SAFE_INTEGER || 9007199254740991;
const noop = (value) => value;
export const toNumber = (value) => {
    if (value === "")
        return undefined;
    const result = Number(value);
    return Number.isNaN(result) ? undefined : result;
};
export const numberToString = (value, precision = 2) => {
    const result = value?.toFixed(precision) ?? "";
    if (result === "NaN")
        return "";
    return result;
};
/**
 * Provides management of number boundaries. This function could easily be merged into useNumberInput
 * but I've left it here because in the source git repo this code was adapted from
 * (https://github.com/siriwatknp/mui-treasury/tree/next), this function also managed the interaction
 * of the spinner controls for increment/decrement of the value. Those spinner controls have been
 * removed from this first version of the AmountInput control, but if we decide we want them back then
 * the management (as per the repo) of those functions would go here.
 * @param options options for boundary conditions
 * @returns number and interface values, and the setter for updating the interface value
 */
export const useNumberBoundary = (options = {}) => {
    const { defaultValue, value, formatter = noop, precision = 2, parser = noop } = options;
    // interface value is the formatted string representation of the number
    const [interfaceValue, setInterfaceValue] = useState(formatter(numberToString(defaultValue, precision)));
    const numberValue = toNumber(parser(interfaceValue));
    useEffect(() => {
        // if a default value, or starting value was passed into the control, then use that value
        if (defaultValue === undefined && value !== numberValue && value !== undefined) {
            setInterfaceValue(formatter(numberToString(value, precision)));
        }
        else if (defaultValue === undefined && value === undefined) {
            setInterfaceValue("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    return {
        numberValue,
        interfaceValue,
        setInterfaceValue: (interfaceValue) => {
            setInterfaceValue(formatter(interfaceValue));
        }
    };
};
function callAllHandlers(...handlers) {
    return (event) => {
        handlers.forEach((handler) => {
            if (typeof handler === "function") {
                handler(event);
            }
        });
    };
}
function getError(value, min, max) {
    if (typeof value === "number") {
        if (value < min)
            return "below-min";
        if (value > max)
            return "exceed-max";
    }
    return null;
}
const useIsFirstMount = () => {
    const isFirstMount = useRef(true);
    useEffect(() => {
        isFirstMount.current = false;
    }, []);
    return isFirstMount.current;
};
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useNumberInput = (options = {}) => {
    const inputRef = useRef(null);
    const { min = minSafeInteger, max = maxSafeInteger, precision = 2, parser = noop, formatter = noop, onChange } = options;
    const { interfaceValue, setInterfaceValue, numberValue } = useNumberBoundary(options);
    const tempInterfaceValue = useRef(interfaceValue);
    useEffect(() => {
        if (process.env.NODE_ENV === "development") {
            if (!inputRef.current) {
                console.warn(`Cannot find inputRef, make sure to pass it to <input /> like this 👇
    
    function NumberInput() {
      const { inputRef } = useNumberInput(options)
      return (
        <input ref={inputRef} />
      )
    }
            `);
            }
        }
    });
    const isFirstMount = useIsFirstMount();
    useEffect(() => {
        if (!isFirstMount) {
            // raise the change event for the initial setting of number to either the passed value or default value
            onChange?.(undefined, numberValue, "change", {
                valueText: interfaceValue,
                error: getError(numberValue, min, max),
                eventType: "change"
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [interfaceValue]);
    const handleChange = (event) => {
        tempInterfaceValue.current = interfaceValue;
        const result = parser(event.target.value);
        if (result.length > 0 && /^(-|\+)?(0|[1-9]\d*)?(\.)?(\d+)?$/.exec(result)) {
            setInterfaceValue(result);
            onChange?.(event, Number(result), "change", {
                valueText: formatter(result),
                error: result?.length > 0 ? getError(Number(result), min, max) : null,
                eventType: "change"
            });
        }
        else {
            setInterfaceValue("");
            onChange?.(event, undefined, "change", {
                valueText: event.target.value,
                error: result?.length > 0 ? getError(Number(result), min, max) : null,
                eventType: "change"
            });
        }
    };
    // on Blur, we apply final formatting and precision
    const handleBlur = (event) => {
        const parsedValue = parser(event.target.value);
        if (parsedValue !== "") {
            const nextNum = Number(parsedValue);
            let result = "";
            if (Number.isNaN(nextNum)) {
                result = tempInterfaceValue.current;
            }
            else {
                result = nextNum.toFixed(precision);
            }
            setInterfaceValue(result);
            onChange?.(event, Number(result), "blur", {
                valueText: formatter(result),
                error: getError(Number(result), min, max),
                eventType: "blur"
            });
        }
        else {
            onChange?.(event, undefined, "blur", {
                valueText: "",
                error: null,
                eventType: "blur"
            });
        }
    };
    return {
        inputRef,
        getInputProps: (handlers) => ({
            pattern: "[0-9]*(.[0-9]+)?",
            "aria-valuemin": min,
            "aria-valuemax": max,
            autoComplete: "off",
            autoCorrect: "off",
            "aria-valuetext": interfaceValue,
            "aria-valuenow": numberValue,
            value: interfaceValue,
            onChange: handleChange,
            onBlur: callAllHandlers(handleBlur, handlers?.onBlur)
        })
    };
};
